import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import { AuthContext } from '@/App';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useDateRange } from '@/contexts/DateRangeContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { 
  Save, RotateCcw, Plus, X, DollarSign, Eye, MousePointer, 
  Target, TrendingUp, TrendingDown, Facebook, Chrome, Music, 
  BarChart3, Search, PieChart, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  BarChart, Bar, PieChart as RechartsPie, Pie, 
  Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORM_CONFIG = {
  meta: { name: 'Meta Ads', color: '#0668E1', icon: Facebook },
  google: { name: 'Google Ads', color: '#EA4335', icon: Chrome },
  tiktok: { name: 'TikTok Ads', color: '#000000', icon: Music },
  ga4: { name: 'GA4', color: '#F9AB00', icon: BarChart3 },
  gsc: { name: 'Search Console', color: '#4285F4', icon: Search }
};

// Widget Types
const WIDGET_TYPES = {
  kpi_spent: { title: 'Gasto Total', icon: DollarSign, color: 'ai-glow' },
  kpi_impressions: { title: 'Impresiones', icon: Eye, color: 'brand-meta' },
  kpi_clicks: { title: 'Clicks', icon: MousePointer, color: 'brand-google' },
  kpi_conversions: { title: 'Conversiones', icon: Target, color: 'success' },
  kpi_ctr: { title: 'CTR Promedio', icon: Activity, color: 'warning' },
  kpi_roi: { title: 'ROI Promedio', icon: TrendingUp, color: 'success' },
  chart_platform_spend: { title: 'Gasto por Plataforma', icon: PieChart },
  chart_platform_comparison: { title: 'Comparación Plataformas', icon: BarChart3 },
  chart_top_campaigns: { title: 'Top Campañas', icon: TrendingUp },
  table_campaigns: { title: 'Tabla de Campañas', icon: Target }
};

const DEFAULT_LAYOUTS = {
  lg: [
    { i: 'kpi_spent', x: 0, y: 0, w: 3, h: 2 },
    { i: 'kpi_impressions', x: 3, y: 0, w: 3, h: 2 },
    { i: 'kpi_clicks', x: 6, y: 0, w: 3, h: 2 },
    { i: 'kpi_conversions', x: 9, y: 0, w: 3, h: 2 },
    { i: 'chart_platform_spend', x: 0, y: 2, w: 6, h: 4 },
    { i: 'chart_platform_comparison', x: 6, y: 2, w: 6, h: 4 },
    { i: 'chart_top_campaigns', x: 0, y: 6, w: 12, h: 4 }
  ],
  md: [
    { i: 'kpi_spent', x: 0, y: 0, w: 3, h: 2 },
    { i: 'kpi_impressions', x: 3, y: 0, w: 3, h: 2 },
    { i: 'kpi_clicks', x: 0, y: 2, w: 3, h: 2 },
    { i: 'kpi_conversions', x: 3, y: 2, w: 3, h: 2 },
    { i: 'chart_platform_spend', x: 0, y: 4, w: 6, h: 4 },
    { i: 'chart_platform_comparison', x: 0, y: 8, w: 6, h: 4 },
    { i: 'chart_top_campaigns', x: 0, y: 12, w: 6, h: 4 }
  ]
};

export default function CustomDashboard() {
  const { token, user } = useContext(AuthContext);
  const { clientId } = useWorkspace();
  const { range: dateRange } = useDateRange();
  const clientIdRef = useRef(clientId);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [layouts, setLayouts] = useState(DEFAULT_LAYOUTS);
  const [activeWidgets, setActiveWidgets] = useState([
    'kpi_spent', 'kpi_impressions', 'kpi_clicks', 'kpi_conversions',
    'chart_platform_spend', 'chart_platform_comparison', 'chart_top_campaigns'
  ]);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddWidget, setShowAddWidget] = useState(false);
  
  // useContainerWidth hook for responsive grid
  const { width, containerRef, mounted } = useContainerWidth();

  useEffect(() => { clientIdRef.current = clientId; }, [clientId]);

  useEffect(() => {
    fetchData();
    loadSavedLayout();
  }, [clientId, dateRange?.label]); // eslint-disable-line

  const fetchData = async () => {
    try {
      const cid = clientIdRef.current;
      const params = new URLSearchParams();
      if (cid) params.set('client_id', cid);
      if (dateRange?.from) params.set('date_from', dateRange.from);
      if (dateRange?.to) params.set('date_to', dateRange.to);
      const qs = params.toString();
      const response = await axios.get(`${API}/campaigns${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSavedLayout = () => {
    const saved = localStorage.getItem(`dashboard_layout_${user?.id}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setLayouts(parsed.layouts || DEFAULT_LAYOUTS);
        setActiveWidgets(parsed.widgets || activeWidgets);
      } catch (e) {
        console.error('Error loading layout:', e);
      }
    }
  };

  const saveLayout = () => {
    localStorage.setItem(`dashboard_layout_${user?.id}`, JSON.stringify({
      layouts,
      widgets: activeWidgets
    }));
    toast.success('Layout guardado');
    setIsEditing(false);
  };

  const resetLayout = () => {
    setLayouts(DEFAULT_LAYOUTS);
    setActiveWidgets([
      'kpi_spent', 'kpi_impressions', 'kpi_clicks', 'kpi_conversions',
      'chart_platform_spend', 'chart_platform_comparison', 'chart_top_campaigns'
    ]);
    localStorage.removeItem(`dashboard_layout_${user?.id}`);
    toast.success('Layout reiniciado');
  };

  const onLayoutChange = (currentLayout, allLayouts) => {
    setLayouts(allLayouts);
  };

  const removeWidget = (widgetId) => {
    setActiveWidgets(prev => prev.filter(w => w !== widgetId));
  };

  const addWidget = (widgetId) => {
    if (!activeWidgets.includes(widgetId)) {
      setActiveWidgets(prev => [...prev, widgetId]);
      // Add to layout
      const newItem = { i: widgetId, x: 0, y: Infinity, w: 3, h: 2 };
      if (widgetId.startsWith('chart_') || widgetId.startsWith('table_')) {
        newItem.w = 6;
        newItem.h = 4;
      }
      setLayouts(prev => ({
        ...prev,
        lg: [...(prev.lg || []), newItem],
        md: [...(prev.md || []), { ...newItem, w: 6 }]
      }));
    }
    setShowAddWidget(false);
  };

  // Calculate metrics
  const metrics = {
    totalSpent: campaigns.reduce((sum, c) => sum + c.spent, 0),
    spentByCurrency: campaigns.reduce((acc, c) => {
      const cur = c.currency || 'CLP';
      acc[cur] = (acc[cur] || 0) + c.spent;
      return acc;
    }, {}),
    totalImpressions: campaigns.reduce((sum, c) => sum + c.impressions, 0),
    totalClicks: campaigns.reduce((sum, c) => sum + c.clicks, 0),
    totalConversions: campaigns.reduce((sum, c) => sum + c.conversions, 0),
    avgCtr: campaigns.length > 0 ? campaigns.reduce((sum, c) => sum + c.ctr, 0) / campaigns.length : 0,
    avgRoi: campaigns.length > 0 ? campaigns.reduce((sum, c) => sum + c.roi, 0) / campaigns.length : 0
  };

  // Platform data
  const platformData = Object.keys(PLATFORM_CONFIG).map(platform => {
    const platformCampaigns = campaigns.filter(c => c.platform === platform);
    return {
      name: PLATFORM_CONFIG[platform].name,
      value: platformCampaigns.reduce((sum, c) => sum + c.spent, 0),
      color: PLATFORM_CONFIG[platform].color,
      campaigns: platformCampaigns.length
    };
  }).filter(p => p.value > 0);

  // Top campaigns
  const topCampaigns = [...campaigns]
    .sort((a, b) => b.spent - a.spent)
    .slice(0, 8)
    .map(c => ({
      name: c.name.substring(0, 15),
      spent: c.spent,
      platform: c.platform
    }));

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const formatCurrency = (num, currency) => {
    const v = parseFloat(num) || 0;
    const c = currency || 'CLP';
    const sym = c === 'EUR' ? '€' : c === 'MXN' ? 'MX$' : '$';
    if (v >= 1000000) return `${sym}${(v/1000000).toFixed(1)}M ${c}`;
    if (v >= 1000) return `${sym}${(v/1000).toFixed(1)}K ${c}`;
    return `${sym}${v.toFixed(0)} ${c}`;
  };

  // Render widget content
  const renderWidget = (widgetId) => {
    const config = WIDGET_TYPES[widgetId];
    if (!config) return null;

    switch (widgetId) {
      case 'kpi_spent':
        return (
          <div className="h-full flex flex-col justify-center p-4">
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign className="w-5 h-5 text-ai-glow" />
              <span className="text-sm text-gray-500">Gasto Total</span>
            </div>
            <div>
              {Object.entries(metrics.spentByCurrency||{}).map(([cur, val]) => (
                <p key={cur} className="font-heading font-bold text-2xl text-gray-900">{formatCurrency(val, cur)} <span className="text-sm text-gray-500">{cur}</span></p>
              ))}
              {(!metrics.spentByCurrency || Object.keys(metrics.spentByCurrency).length === 0) && (
                <p className="font-heading font-bold text-3xl text-gray-900">{formatCurrency(metrics.totalSpent)}</p>
              )}
            </div>
          </div>
        );
      
      case 'kpi_impressions':
        return (
          <div className="h-full flex flex-col justify-center p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Eye className="w-5 h-5 text-brand-meta" />
              <span className="text-sm text-gray-500">Impresiones</span>
            </div>
            <p className="font-heading font-bold text-3xl text-gray-900">{formatNumber(metrics.totalImpressions)}</p>
          </div>
        );
      
      case 'kpi_clicks':
        return (
          <div className="h-full flex flex-col justify-center p-4">
            <div className="flex items-center space-x-2 mb-2">
              <MousePointer className="w-5 h-5 text-brand-google" />
              <span className="text-sm text-gray-500">Clicks</span>
            </div>
            <p className="font-heading font-bold text-3xl text-gray-900">{formatNumber(metrics.totalClicks)}</p>
          </div>
        );
      
      case 'kpi_conversions':
        return (
          <div className="h-full flex flex-col justify-center p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Target className="w-5 h-5 text-success" />
              <span className="text-sm text-gray-500">Conversiones</span>
            </div>
            <p className="font-heading font-bold text-3xl text-gray-900">{formatNumber(metrics.totalConversions)}</p>
          </div>
        );

      case 'kpi_ctr':
        return (
          <div className="h-full flex flex-col justify-center p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Activity className="w-5 h-5 text-warning" />
              <span className="text-sm text-gray-500">CTR Promedio</span>
            </div>
            <p className="font-heading font-bold text-3xl text-gray-900">{metrics.avgCtr.toFixed(2)}%</p>
          </div>
        );

      case 'kpi_roi':
        return (
          <div className="h-full flex flex-col justify-center p-4">
            <div className="flex items-center space-x-2 mb-2">
              {metrics.avgRoi >= 0 ? (
                <TrendingUp className="w-5 h-5 text-success" />
              ) : (
                <TrendingDown className="w-5 h-5 text-error" />
              )}
              <span className="text-sm text-gray-500">ROI Promedio</span>
            </div>
            <p className={`font-heading font-bold text-3xl ${metrics.avgRoi >= 0 ? 'text-success' : 'text-error'}`}>
              {metrics.avgRoi.toFixed(1)}%
            </p>
          </div>
        );

      case 'chart_platform_spend':
        return (
          <div className="h-full p-4">
            <h4 className="text-sm font-medium mb-2">Gasto por Plataforma</h4>
            <ResponsiveContainer width="100%" height="85%">
              <RechartsPie>
                <Pie
                  data={platformData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {platformData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
        );

      case 'chart_platform_comparison':
        return (
          <div className="h-full p-4">
            <h4 className="text-sm font-medium mb-2">Comparación Plataformas</h4>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={platformData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis type="number" stroke="#888" tickFormatter={formatCurrency} />
                <YAxis type="category" dataKey="name" stroke="#888" width={80} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {platformData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );

      case 'chart_top_campaigns':
        return (
          <div className="h-full p-4">
            <h4 className="text-sm font-medium mb-2">Top Campañas por Gasto</h4>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={topCampaigns}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" stroke="#888" angle={-45} textAnchor="end" height={60} />
                <YAxis stroke="#888" tickFormatter={formatCurrency} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="spent" radius={[4, 4, 0, 0]}>
                  {topCampaigns.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PLATFORM_CONFIG[entry.platform]?.color || '#888'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );

      default:
        return <div className="p-4">Widget: {widgetId}</div>;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-primary text-xl">Cargando dashboard...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="custom-dashboard">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <div>
            <h1 className="font-heading font-bold text-4xl mb-2">Dashboard Personalizable</h1>
            <p className="text-secondary">Arrastra y redimensiona los widgets a tu gusto</p>
          </div>
          
          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <Button
                  onClick={() => setShowAddWidget(true)}
                  variant="outline"
                  className="rounded-full border-white/20"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Widget
                </Button>
                <Button
                  onClick={resetLayout}
                  variant="outline"
                  className="rounded-full border-white/20"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reiniciar
                </Button>
                <Button
                  onClick={saveLayout}
                  className="bg-success text-white rounded-full"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Guardar
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setIsEditing(true)}
                className="bg-white text-black hover:bg-gray-200 rounded-full"
              >
                Editar Dashboard
              </Button>
            )}
          </div>
        </motion.div>

        {/* Grid Layout */}
        <div ref={containerRef} style={{ width: '100%' }}>
          {mounted && width > 0 && (
            <ResponsiveGridLayout
              className="layout"
              width={width}
              layouts={layouts}
              onLayoutChange={onLayoutChange}
              breakpoints={{ lg: 1200, md: 768, sm: 480 }}
              cols={{ lg: 12, md: 6, sm: 2 }}
              rowHeight={60}
              isDraggable={isEditing}
              isResizable={isEditing}
              compactType="vertical"
              preventCollision={false}
            >
              {activeWidgets.map((widgetId) => (
                <div
                  key={widgetId}
                  className={`glass-card overflow-hidden ${isEditing ? 'cursor-move border-dashed border-white/30' : ''}`}
                >
                  {isEditing && (
                    <button
                      onClick={() => removeWidget(widgetId)}
                      className="absolute top-2 right-2 z-10 p-1 rounded-full bg-error/80 hover:bg-error"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {renderWidget(widgetId)}
                </div>
              ))}
            </ResponsiveGridLayout>
          )}
        </div>

        {/* Add Widget Modal */}
        {showAddWidget && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-card p-8 w-full max-w-lg"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-bold text-xl">Agregar Widget</h2>
                <button onClick={() => setShowAddWidget(false)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(WIDGET_TYPES).map(([id, config]) => {
                  const isActive = activeWidgets.includes(id);
                  const Icon = config.icon;
                  return (
                    <button
                      key={id}
                      onClick={() => !isActive && addWidget(id)}
                      disabled={isActive}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        isActive 
                          ? 'border-white/10 bg-white/5 opacity-50 cursor-not-allowed' 
                          : 'border-white/20 hover:border-ai-glow hover:bg-ai-glow/10'
                      }`}
                    >
                      <Icon className="w-5 h-5 mb-2" />
                      <p className="font-medium text-sm">{config.title}</p>
                      {isActive && <p className="text-xs text-secondary mt-1">Ya agregado</p>}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
